import { t } from '@/lib/i18n';
import { useParams } from '@tanstack/react-router';
import { Download, Package } from 'lucide-react';
import { CenteredSpinner } from '@/components/ui';
import { KIND_ICON, KIND_TINT, cn } from '@/lib/format';
import { shareUrls } from '@/lib/api';
import { useSharedView } from '@/lib/queries';

/**
 * The recipient's view of a share link — the one page in the app served to
 * people with no account, replacing Pimcore's `shared_link.html.twig`.
 *
 * Deliberately standalone: no sidebar, no nav, nothing that hints at a wider
 * library. A recipient sees exactly the assets in their link and two ways to
 * take them (all-as-zip, or one at a time), which is what the original offered.
 *
 * Every failure mode — unknown token, expired, revoked — renders the same
 * message, so the page can't be used to probe which tokens exist.
 */
export function SharedPage() {
  const { token } = useParams({ from: '/share/$token' });
  const { data, isPending, error } = useSharedView(token);

  if (isPending) return <CenteredSpinner label={t('sharedPage.loading')} />;

  if (error || !data) {
    return (
      <div className="flex min-h-dvh items-center justify-center surface-page px-6">
        <div className="max-w-sm text-center">
          <Package className="mx-auto mb-3 size-10 text-faint" />
          <h1 className="text-lg font-semibold text-strong">{t('sharedPage.linkUnavailable')}</h1>
          <p className="mt-1 text-sm text-muted">{t('sharedPage.linkUnavailableDetail')}</p>
        </div>
      </div>
    );
  }

  const expires = new Date(data.expiresAt);

  return (
    <div className="min-h-dvh surface-page">
      <div className="w-full px-6 py-8">
        <header className="mb-6">
          <h1 className="text-xl font-semibold text-strong">{data.title ?? t('sharedPage.sharedAssets')}</h1>
          <p className="mt-1 text-sm text-muted">
            {data.assets.length === 1
              ? t('sharedPage.oneFile', { n: data.assets.length })
              : t('sharedPage.nFiles', { n: data.assets.length })}{' '}
            ·{' '}
            {t('sharedPage.availableUntil', {
              date: expires.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }),
            })}
          </p>
          {data.assets.length > 0 && (
            <a
              href={shareUrls.zip(token)}
              className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-500 px-4 text-sm font-medium text-white transition-opacity hover:opacity-90"
            >
              <Download className="size-4" /> {t('sharedPage.downloadAllZip')}
            </a>
          )}
        </header>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {data.assets.map((a) => {
            const Icon = KIND_ICON[a.kind];
            const src = shareUrls.asset(token, a.assetId);
            return (
              <figure
                key={a.assetId}
                className="overflow-hidden rounded-[var(--radius-card)] surface-card border line"
              >
                <div className="flex aspect-square items-center justify-center surface-sunken">
                  {a.kind === 'image' ? (
                    // Loads the asset at its shared size, so what you preview is
                    // what the download gives you.
                    <img src={src} alt={a.fileName} className="size-full object-contain" loading="lazy" />
                  ) : (
                    <Icon className={cn('size-12', KIND_TINT[a.kind])} />
                  )}
                </div>
                <figcaption className="border-t line p-3">
                  <p className="truncate text-sm text-body" title={a.fileName}>
                    {a.fileName}
                  </p>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-[11px] text-faint">
                      {a.width && a.height ? `${a.width}×${a.height}` : a.kind}
                    </span>
                    <a
                      href={src}
                      download={a.fileName}
                      className="inline-flex items-center gap-1 text-xs text-brand-500 hover:underline"
                    >
                      <Download className="size-3" /> {t('sharedPage.download')}
                    </a>
                  </div>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </div>
  );
}
